import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Crop, getCrops, loadCrops } from '@next/arable-lib/crop';
import { CropField } from '@next/arable-lib/crop-field';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { DateService } from '../../../shared/services/date.service';
import { CropFieldSelection } from '../../shared/constants';
import { TgaCropFieldWithStatus, TgaSteps } from '../../shared/models/models';
import { TgaSharedService } from '../../shared/services/tga-shared.service';
import { loadCropFields, StepperActions } from '../../store/tga.actions';
import { getTgaCropYear, selectCropFields } from '../../store/tga.selectors';
import { BackArrowButtonComponent } from '../back-arrow-button/back-arrow-button.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CropYearSelectorComponent } from '../../../shared/components/crop-year-selector/crop-year-selector.component';

interface TgaItem {
  label: string;
  sublabel?: string;
  route: string;
  cropFieldId?: number;
}

interface TgaSection {
  items: TgaItem[];
}

interface TgaTab {
  translationKey: string;
  sections: TgaSection[];
}

@Component({
    selector: 'app-tga-cropfields-selection',
    templateUrl: './tga-cropfields-selection.component.html',
    styleUrl: './tga-cropfields-selection.component.scss',
    imports: [
        BackArrowButtonComponent,
        CropYearSelectorComponent,
        MatButton,
        MatIcon,
        TranslatePipe,
    ],
})
export class TgaCropfieldsSelectionComponent implements OnInit, OnDestroy {
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  readonly cropFields = signal<TgaCropFieldWithStatus[]>([]);
  readonly crops = signal<Crop[]>([]);
  readonly data = signal<TgaTab[]>([]);
  readonly activeTabIndex = signal<number>(0);
  readonly activeTab = computed<TgaTab>(
    () => this.data()[this.activeTabIndex()] || { translationKey: '', sections: [] },
  );
  readonly currentActionName = computed<string>(() => this.activeTab().translationKey);
  readonly activeSections = computed<TgaSection[]>(() => this.activeTab().sections);

  // Computed translation keys to avoid function calls in template
  readonly activeTabTranslationKey = computed<string>(() =>
    'TGA.Selection.' + this.activeTab().translationKey,
  );

  readonly sectionTranslationKeys = computed<string[]>(() => {
    const baseKey = this.activeTabTranslationKey();
    return this.activeSections().map((_, index) => `${baseKey}.Section.${index}`);
  });
  private readonly destroy$ = new Subject<void>();
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tgaSharedService = inject(TgaSharedService);
  private readonly locale = inject(LocaleService);
  private readonly dateService = inject(DateService);
  readonly currentYear = signal<number>(this.dateService.currentYear());
  private hasLoadedData = false;
  private hasLoadedReferenceData = false;

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(takeUntil(this.destroy$))
      .subscribe((year) => {
        this.currentYear.set(year);
        if (!this.hasLoadedData) {
          this.hasLoadedData = true;
          this.loadData();
        }
      });
    this.setupDataSubscriptions();
    this.ai.trackEvent(CropFieldSelection.pageViewed, {});
    this.ai.trackEvent(CropFieldSelection.adviceItemOpened, {});
  }

  onItemClick(item: TgaItem): void {
    if (this.activeTab().translationKey === 'Advices') {
      this.ai.trackEvent(CropFieldSelection.adviceItemOpened, {});
    } else if (!item.cropFieldId) {
      this.ai.trackEvent(CropFieldSelection.genericQuestionnaireOpened, {});
    } else {
      this.ai.trackEvent(CropFieldSelection.questionnaireItemOpened, {});
    }
    this.router.navigate([item.route]);
  }

  getCropName(cropId: number | undefined): string {
    return this.tgaSharedService.getCropName(cropId, this.crops());
  }

  getEndDateYear(cropField: CropField): number {
    return this.tgaSharedService.getEndDateYear(cropField, this.currentYear());
  }

  navigateToTGAInsights = (): void => {
    this.router.navigate(['/tga']);
  };

  selectTab(index: number): void {
    if (index < 0 || index >= this.data().length) {
      return;
    }
    this.activeTabIndex.set(index);
    if (this.activeTab().translationKey === 'Advices') {
      this.ai.trackEvent(CropFieldSelection.adviceItemOpened, {});
    } else if (this.activeTab().translationKey === 'Questionnaires') {
      this.ai.trackEvent(CropFieldSelection.genericQuestionnaireOpened, {});
    }
  }

  isTabActive(index: number): boolean {
    return this.activeTabIndex() === index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.store.dispatch(loadCropFields({ cropYear: this.currentYear() }));
    if (!this.hasLoadedReferenceData) {
      this.hasLoadedReferenceData = true;
      this.store.dispatch(loadCrops());
    }
    this.store.dispatch(StepperActions.setActiveStepByKey({ stepKey: TgaSteps.General }));
  }

  private setupDataSubscriptions(): void {
    combineLatest([this.store.select(selectCropFields), this.store.select(getCrops)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cropFields, crops]) => {
        this.cropFields.set(cropFields || []);
        this.crops.set(crops || []);
        this.generateTgaData();
      });
  }

  private generateCropFieldItem(cropField: CropField, routePrefix: string): TgaItem {
    return {
      label: cropField.name,
      sublabel: `${cropField.areaInHectares} ha ${this.getCropName(cropField.cropId)} - ${this.getEndDateYear(cropField)}`,
      route: `${routePrefix}/${this.currentYear()}/${cropField.id}`,
      cropFieldId: cropField.id,
    };
  }

  private generateTgaData(): void {
    const cropFields = this.cropFields();
    const cropFieldAdviceItems = cropFields.map((cf) => {
      const stepName = cf.questionnaireStatus.recommendedStartingStep;
      return this.generateCropFieldItem(
        cf.cropField,
        `/tga/cropfields/${stepName}/advice/specific`,
      );
    });
    const cropFieldQuestionnaireItems = cropFields.map((cf) => {
      const stepName = cf.questionnaireStatus.recommendedStartingStep;
      return this.generateCropFieldItem(cf.cropField,
        `/tga/cropfields/${stepName}/questionnaire/specific`,
      )
    },
    );

    const newData: TgaTab[] = [
      {
        translationKey: 'Advices',
        sections: [{ items: cropFieldAdviceItems }],
      },
      {
        translationKey: 'Questionnaires',
        sections: [
          {
            items: [
              {
                label: this.locale.translate('TGA.Selection.GenericLabel'),
                route: `/tga/cropfields/general/questionnaire/generic/${this.currentYear()}`,
              },
            ],
          },
          {
            items: cropFieldQuestionnaireItems,
          },
        ],
      },
    ];

    this.data.set(newData);
  }
}
